import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';
const DOC_TYPES = ['Order Confirmation', 'Subpoena', 'Authorization', 'Invoice', 'Other'];

// ── Icons ─────────────────────────────────────────────────────────────────────
const FolderIcon = ({ open }) => (
  <svg className="w-4 h-4 text-yellow-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
    {open
      ? <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
      : <path fillRule="evenodd" d="M2 6a2 2 0 012-2h4l2 2h6a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clipRule="evenodd" />}
  </svg>
);
const ChevronIcon = ({ open }) => (
  <svg className={`w-3.5 h-3.5 text-gray-400 transition-transform flex-shrink-0 ${open ? 'rotate-90' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
  </svg>
);
const DocIcon = () => (
  <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

export default function TemplateLibrary() {
  const [templates, setTemplates] = useState([]);
  const [folders, setFolders] = useState([]);
  const [selectedFolderId, setSelectedFolderId] = useState(null); // null = root
  const [openFolders, setOpenFolders] = useState({});
  const [showTemplateForm, setShowTemplateForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ name: '', description: '', document_type: 'Order Confirmation', folder_id: null });
  const [uploadFile, setUploadFile] = useState(null);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [newFolderParent, setNewFolderParent] = useState(null);
  const [renamingFolder, setRenamingFolder] = useState(null);
  const [renamingName, setRenamingName] = useState('');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    const [tRes, fRes] = await Promise.all([
      axios.get(`${API_URL}/api/templates`, { headers }),
      axios.get(`${API_URL}/api/template-folders`, { headers }),
    ]);
    setTemplates(tRes.data);
    setFolders(fRes.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Folder tree helpers ───────────────────────────────────────────────────
  const rootFolders = folders.filter(f => !f.parent_id);
  const childFolders = (parentId) => folders.filter(f => f.parent_id === parentId);
  const toggleFolder = (id) => setOpenFolders(o => ({ ...o, [id]: !o[id] }));

  const createFolder = async () => {
    if (!newFolderName.trim()) return;
    await axios.post(`${API_URL}/api/template-folders`, { name: newFolderName.trim(), parent_id: newFolderParent }, { headers });
    setNewFolderName('');
    setShowNewFolder(false);
    setNewFolderParent(null);
    load();
  };

  const renameFolder = async (folderId) => {
    if (!renamingName.trim()) return;
    await axios.put(`${API_URL}/api/template-folders/${folderId}`, { name: renamingName.trim() }, { headers });
    setRenamingFolder(null);
    load();
  };

  const deleteFolder = async (folderId) => {
    if (!window.confirm('Delete this folder? Templates inside will be moved to root.')) return;
    await axios.delete(`${API_URL}/api/template-folders/${folderId}`, { headers });
    if (selectedFolderId === folderId) setSelectedFolderId(null);
    load();
  };

  // ── Template actions ──────────────────────────────────────────────────────
  const convertAndDownload = async () => {
    if (!uploadFile) { alert('Select a .docx file first'); return; }
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      const res = await axios.post(`${API_URL}/api/templates/convert`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }, responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = uploadFile.name.replace('.docx', '_jinja2.docx');
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) { alert('Conversion failed: ' + err.message); }
  };

  const save = async () => {
    try {
      const folderId = form.folder_id ?? selectedFolderId;
      if (uploadFile) {
        const fd = new FormData();
        fd.append('file', uploadFile);
        fd.append('name', form.name);
        fd.append('description', form.description);
        fd.append('document_type', form.document_type);
        if (folderId) fd.append('folder_id', folderId);
        await axios.post(`${API_URL}/api/templates/upload`, fd, {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' }
        });
      } else if (editingId) {
        await axios.put(`${API_URL}/api/templates/${editingId}`, { ...form, folder_id: folderId }, { headers });
      } else {
        await axios.post(`${API_URL}/api/templates`, { ...form, folder_id: folderId }, { headers });
      }
      setShowTemplateForm(false);
      setEditingId(null);
      setUploadFile(null);
      setForm({ name: '', description: '', document_type: 'Order Confirmation', folder_id: null });
      load();
    } catch (err) { alert('Failed: ' + (err.response?.data?.detail || err.message)); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await axios.delete(`${API_URL}/api/templates/${id}`, { headers });
    load();
  };

  const startEdit = (t) => {
    setForm({ name: t.name, description: t.description || '', document_type: t.document_type, template_html: '', folder_id: t.folder_id || null });
    setEditingId(t.id);
    axios.get(`${API_URL}/api/templates/${t.id}`, { headers }).then(res => {
      setForm(f => ({ ...f, template_html: res.data.template_html || '' }));
    });
    setShowTemplateForm(true);
  };

  const moveTemplate = async (templateId, newFolderId) => {
    await axios.put(`${API_URL}/api/templates/${templateId}`, { folder_id: newFolderId || null }, { headers });
    load();
  };

  // ── Templates in current view ─────────────────────────────────────────────
  const visibleTemplates = templates.filter(t =>
    selectedFolderId === null
      ? !t.folder_id  // root: show unfoldered
      : t.folder_id === selectedFolderId
  );

  // ── Folder tree node ──────────────────────────────────────────────────────
  const FolderNode = ({ folder, depth = 0 }) => {
    const isOpen = openFolders[folder.id];
    const isSelected = selectedFolderId === folder.id;
    const children = childFolders(folder.id);
    const isRenaming = renamingFolder === folder.id;

    return (
      <div>
        <div
          className={`flex items-center gap-1 px-2 py-1.5 rounded cursor-pointer group transition-colors ${isSelected ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100'}`}
          style={{ paddingLeft: `${8 + depth * 16}px` }}
          onClick={() => { setSelectedFolderId(folder.id); if (!isOpen) toggleFolder(folder.id); }}
        >
          {children.length > 0 ? (
            <span onClick={e => { e.stopPropagation(); toggleFolder(folder.id); }}>
              <ChevronIcon open={isOpen} />
            </span>
          ) : <span className="w-3.5" />}
          <FolderIcon open={isOpen && isSelected} />
          {isRenaming ? (
            <input
              autoFocus
              value={renamingName}
              onChange={e => setRenamingName(e.target.value)}
              onBlur={() => renameFolder(folder.id)}
              onKeyDown={e => { if (e.key === 'Enter') renameFolder(folder.id); if (e.key === 'Escape') setRenamingFolder(null); }}
              onClick={e => e.stopPropagation()}
              className="flex-1 text-sm px-1 border border-blue-400 rounded outline-none"
            />
          ) : (
            <span className="flex-1 text-sm truncate">{folder.name}</span>
          )}
          <div className="hidden group-hover:flex items-center gap-1">
            <button title="New subfolder" onClick={e => { e.stopPropagation(); setNewFolderParent(folder.id); setNewFolderName(''); setShowNewFolder(true); }}
              className="text-gray-400 hover:text-blue-600 text-xs px-0.5">＋</button>
            <button title="Rename" onClick={e => { e.stopPropagation(); setRenamingFolder(folder.id); setRenamingName(folder.name); }}
              className="text-gray-400 hover:text-yellow-600 text-xs px-0.5">✏</button>
            <button title="Delete" onClick={e => { e.stopPropagation(); deleteFolder(folder.id); }}
              className="text-gray-400 hover:text-red-600 text-xs px-0.5">✕</button>
          </div>
        </div>
        {isOpen && children.map(child => (
          <FolderNode key={child.id} folder={child} depth={depth + 1} />
        ))}
      </div>
    );
  };

  // ── Breadcrumb ────────────────────────────────────────────────────────────
  const getBreadcrumb = () => {
    if (!selectedFolderId) return [{ id: null, name: 'Root' }];
    const crumbs = [];
    let current = folders.find(f => f.id === selectedFolderId);
    while (current) {
      crumbs.unshift(current);
      current = current.parent_id ? folders.find(f => f.id === current.parent_id) : null;
    }
    return [{ id: null, name: 'Root' }, ...crumbs];
  };

  return (
    <div className="flex gap-0 h-full min-h-screen">
      {/* ── Sidebar ── */}
      <div className="w-56 flex-shrink-0 bg-white border-r border-gray-200 flex flex-col">
        <div className="flex items-center justify-between px-3 py-3 border-b border-gray-100">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Folders</span>
          <button title="New root folder" onClick={() => { setNewFolderParent(null); setNewFolderName(''); setShowNewFolder(true); }}
            className="text-gray-400 hover:text-blue-600 text-lg leading-none">＋</button>
        </div>

        {/* Root */}
        <div
          className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer transition-colors ${selectedFolderId === null ? 'bg-blue-50 text-blue-700' : 'hover:bg-gray-100 text-gray-700'}`}
          onClick={() => setSelectedFolderId(null)}
        >
          <svg className="w-4 h-4 text-gray-400 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" /></svg>
          <span className="text-sm">All (Unfiled)</span>
        </div>

        {/* Folder tree */}
        <div className="flex-1 overflow-y-auto py-1">
          {rootFolders.map(f => <FolderNode key={f.id} folder={f} />)}
        </div>

        {/* New folder input */}
        {showNewFolder && (
          <div className="border-t p-2">
            <div className="text-xs text-gray-500 mb-1">{newFolderParent ? 'New subfolder in: ' + (folders.find(f => f.id === newFolderParent)?.name || '') : 'New root folder'}</div>
            <input
              autoFocus
              value={newFolderName}
              onChange={e => setNewFolderName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') createFolder(); if (e.key === 'Escape') setShowNewFolder(false); }}
              placeholder="Folder name..."
              className="w-full text-sm border rounded px-2 py-1 mb-1 outline-none focus:ring-1 focus:ring-blue-400"
            />
            <div className="flex gap-1">
              <button onClick={createFolder} className="flex-1 text-xs bg-blue-600 text-white rounded px-2 py-1">Create</button>
              <button onClick={() => setShowNewFolder(false)} className="flex-1 text-xs bg-gray-100 rounded px-2 py-1">Cancel</button>
            </div>
          </div>
        )}
      </div>

      {/* ── Main content ── */}
      <div className="flex-1 p-6 overflow-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Template Library</h1>
            {/* Breadcrumb */}
            <div className="flex items-center gap-1 text-sm text-gray-500 mt-1">
              {getBreadcrumb().map((crumb, i, arr) => (
                <span key={crumb.id ?? 'root'} className="flex items-center gap-1">
                  <button onClick={() => setSelectedFolderId(crumb.id)} className={`hover:text-blue-600 ${i === arr.length - 1 ? 'text-gray-800 font-medium' : ''}`}>{crumb.name}</button>
                  {i < arr.length - 1 && <span className="text-gray-300">/</span>}
                </span>
              ))}
            </div>
          </div>
          <div className="flex gap-2">
            {selectedFolderId && (
              <button onClick={() => { setNewFolderParent(selectedFolderId); setNewFolderName(''); setShowNewFolder(true); }}
                className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded text-sm hover:bg-gray-50">
                + Subfolder
              </button>
            )}
            <button onClick={() => { setShowTemplateForm(true); setEditingId(null); setUploadFile(null); setForm({ name: '', description: '', document_type: 'Order Confirmation', folder_id: selectedFolderId }); }}
              className="px-4 py-2 text-white rounded text-sm" style={{ backgroundColor: '#1a2744' }}>
              + New Template
            </button>
          </div>
        </div>

        {/* Template form */}
        {showTemplateForm && (
          <div className="bg-white rounded-lg shadow p-6 mb-6 border border-gray-200">
            <h3 className="font-semibold mb-4">{editingId ? 'Edit Template' : 'New Template'}</h3>
            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Template Name *</label>
                <input value={form.name} onChange={e => setForm(f => ({...f, name: e.target.value}))} className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Folder</label>
                <select
                  value={form.folder_id ?? selectedFolderId ?? ''}
                  onChange={e => setForm(f => ({...f, folder_id: e.target.value || null}))}
                  className="w-full border rounded px-2 py-1.5 text-sm bg-white"
                >
                  <option value="">Root (Unfiled)</option>
                  {folders.map(f => <option key={f.id} value={f.id}>{f.parent_id ? '  └ ' : ''}{f.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Upload .docx Template File</label>
                <input type="file" accept=".docx" onChange={e => setUploadFile(e.target.files[0])} className="w-full text-sm border rounded px-2 py-1.5" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Document Type</label>
                <select value={form.document_type} onChange={e => setForm(f => ({...f, document_type: e.target.value}))} className="w-full border rounded px-2 py-1.5 text-sm bg-white">
                  {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="w-full border rounded px-2 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Template HTML (optional)</label>
                <textarea value={form.template_html || ''} onChange={e => setForm(f => ({...f, template_html: e.target.value}))}
                  rows={6} className="w-full border rounded px-2 py-1.5 text-sm font-mono text-xs" placeholder="Paste HTML template with {{FieldName}} merge tags..." />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={save} className="px-4 py-2 bg-blue-600 text-white rounded text-sm">Save Template</button>
              <button onClick={convertAndDownload} className="px-4 py-2 bg-green-600 text-white rounded text-sm">⚡ Convert MERGEFIELD → Jinja2</button>
              <button onClick={() => { setShowTemplateForm(false); setEditingId(null); }} className="px-4 py-2 bg-gray-200 rounded text-sm">Cancel</button>
            </div>
          </div>
        )}

        {/* Subfolders in current location */}
        {(() => {
          const subs = selectedFolderId ? childFolders(selectedFolderId) : rootFolders;
          return subs.length > 0 ? (
            <div className="mb-5">
              <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Folders</h4>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {subs.map(f => (
                  <div key={f.id}
                    onDoubleClick={() => setSelectedFolderId(f.id)}
                    onClick={() => setSelectedFolderId(f.id)}
                    className="flex flex-col items-center gap-1 p-3 bg-white border border-gray-200 rounded-lg cursor-pointer hover:border-blue-300 hover:bg-blue-50 transition-colors group"
                  >
                    <svg className="w-8 h-8 text-yellow-400" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
                    </svg>
                    <span className="text-xs text-center text-gray-700 truncate w-full text-center">{f.name}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : null;
        })()}

        {/* Templates in current folder */}
        <div>
          {visibleTemplates.length > 0 && (
            <h4 className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Templates</h4>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {visibleTemplates.map(t => (
              <div key={t.id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:border-blue-300 transition-colors">
                <div className="flex items-start gap-2 mb-2">
                  <DocIcon />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-gray-800 text-sm truncate">{t.name}</div>
                    <div className="text-xs text-gray-400">{t.document_type}</div>
                  </div>
                  {t.has_docx && <span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-700 rounded flex-shrink-0">📄</span>}
                </div>
                {t.description && <div className="text-xs text-gray-500 mb-2">{t.description}</div>}
                <div className="text-xs text-gray-400 mb-3">By {t.created_by} · {t.created_at?.split('T')[0]}</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <button onClick={() => startEdit(t)} className="text-xs text-blue-600 hover:underline">Edit</button>
                  <button onClick={() => del(t.id)} className="text-xs text-red-600 hover:underline">Delete</button>
                  <select
                    value={t.folder_id || ''}
                    onChange={e => moveTemplate(t.id, e.target.value || null)}
                    className="ml-auto text-xs border border-gray-200 rounded px-1 py-0.5 bg-white text-gray-500"
                    title="Move to folder"
                  >
                    <option value="">Move to...</option>
                    <option value="">Root (Unfiled)</option>
                    {folders.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                  </select>
                </div>
              </div>
            ))}
          </div>
          {visibleTemplates.length === 0 && !showTemplateForm && (
            <div className="text-center text-gray-400 py-16 text-sm">
              {selectedFolderId ? 'This folder is empty.' : 'No unfiled templates.'} Click &quot;+ New Template&quot; to add one.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
